import NotesApi from '../data/api/notes-api.js';
import {
  BREAKPOINT_MD,
  TAB_NOTES,
  TAB_ARCHIVE,
  animateModalShow,
  animateModalHide,
  animateNoteListItems,
  animateMobileDetailShow,
  animateMobileDetailHide,
  initTheme,
  toggleTheme,
  pushHistory,
  goBack,
  showError,
} from '../utils/index.js';

const home = () => {
  // Desktop elements
  const sidebar = document.querySelector('#sidebar');
  const noteListContainer = document.querySelector('#noteListContainer');
  const desktopNoteList = noteListContainer?.querySelector('note-list');
  const desktopNoteDetail = document.querySelector('main note-detail');
  const navTabs = document.querySelector('nav-tabs');
  const desktopSearchBar = sidebar?.querySelector('search-bar');
  const newNoteBtn = document.querySelector('#newNoteBtn');
  const themeToggle = document.querySelector('#themeToggle');

  // Mobile elements
  const mobileNoteListContainer = document.querySelector('#mobileNoteListContainer');
  const mobileNoteList = mobileNoteListContainer?.querySelector('note-list');
  const mobileNoteDetail = document.querySelector('#mobileNoteDetail');
  const mobileNoteDetailComponent = mobileNoteDetail?.querySelector('note-detail');
  const mobileSearchBar = document.querySelector('.mobile-layout search-bar');
  const bottomNav = document.querySelector('bottom-nav');
  const mobileThemeToggle = document.querySelector('#mobileThemeToggle');
  const mobileTitle = document.querySelector('#mobileTitle');

  // Modal & Loading
  const noteFormModal = document.querySelector('#noteFormModal');
  const loadingIndicator = document.querySelector('#loadingIndicator');

  // State
  let currentTab = TAB_NOTES;
  let currentQuery = '';
  let selectedNote = null;
  let activeNotes = [];
  let archivedNotes = [];

  // Loading indicator helpers
  const showLoading = () => loadingIndicator?.show();
  const hideLoading = () => loadingIndicator?.hide();

  // Client-side search
  const searchNotes = (notes, query) => {
    if (!query || query.trim() === '') {
      return notes;
    }

    const loweredQuery = query.toLowerCase().trim();
    const jammedQuery = loweredQuery.replace(/\s/g, '');

    return notes.filter((note) => {
      const loweredTitle = (note.title || '').toLowerCase();
      const loweredBody = (note.body || '').toLowerCase();
      const jammedTitle = loweredTitle.replace(/\s/g, '');
      const jammedBody = loweredBody.replace(/\s/g, '');

      return jammedTitle.includes(jammedQuery) || jammedBody.includes(jammedQuery);
    });
  };

  // Fetch notes from API
  const fetchNotes = async () => {
    showLoading();
    try {
      const [active, archived] = await Promise.all([
        NotesApi.getNotes(),
        NotesApi.getArchivedNotes(),
      ]);
      activeNotes = active;
      archivedNotes = archived;
    } catch (error) {
      showError('Failed to load notes. Please try again.');
      activeNotes = [];
      archivedNotes = [];
    } finally {
      hideLoading();
    }
  };

  // Update tab counts
  const updateTabCounts = () => {
    navTabs?.setCount(activeNotes.length, archivedNotes.length);
  };

  // Display notes (uses cached data)
  const displayNotes = () => {
    const isArchived = currentTab === TAB_ARCHIVE;
    const sourceNotes = isArchived ? archivedNotes : activeNotes;
    const notes = searchNotes(sourceNotes, currentQuery);

    // Update desktop list
    if (desktopNoteList) {
      desktopNoteList.notes = notes;
      animateNoteListItems(desktopNoteList);
    }

    // Update mobile list
    if (mobileNoteList) {
      mobileNoteList.notes = notes;
      animateNoteListItems(mobileNoteList);
    }

    // Update mobile title
    if (mobileTitle) {
      mobileTitle.textContent = isArchived ? 'Archive' : 'My Notes';
    }

    updateTabCounts();

    // Select first note if none selected (desktop only)
    if (notes.length > 0 && !selectedNote && window.innerWidth >= BREAKPOINT_MD) {
      selectNote(notes[0]);
    }

    // Update selection in list
    if (desktopNoteList && selectedNote) {
      desktopNoteList.setSelectedNote(selectedNote.id);
    }
  };

  // Select note
  const selectNote = (note) => {
    selectedNote = note;

    // Update desktop detail
    if (desktopNoteDetail) {
      desktopNoteDetail.note = note;
    }

    // Update mobile detail
    if (mobileNoteDetailComponent) {
      mobileNoteDetailComponent.note = note;
    }

    // Update selection in list
    if (desktopNoteList) {
      desktopNoteList.setSelectedNote(note?.id);
    }
  };

  // Show mobile note detail
  const showMobileNoteDetail = (note) => {
    pushHistory({ view: 'detail', noteId: note.id });
    selectNote(note);
    if (mobileNoteDetail) {
      mobileNoteDetail.classList.add('active');
      animateMobileDetailShow(mobileNoteDetail);
    }
  };

  // Hide mobile note detail
  const hideMobileNoteDetail = async () => {
    if (mobileNoteDetail) {
      await animateMobileDetailHide(mobileNoteDetail);
      mobileNoteDetail.classList.remove('active');
    }
  };

  // Show note form modal
  const showNoteFormModal = () => {
    pushHistory({ view: 'modal' });
    if (noteFormModal) {
      noteFormModal.classList.add('active');
      const container = noteFormModal.querySelector('.note-form-container');
      animateModalShow(container);
    }
  };

  // Hide note form modal
  const hideNoteFormModal = async () => {
    if (noteFormModal) {
      const container = noteFormModal.querySelector('.note-form-container');
      await animateModalHide(container);
      noteFormModal.classList.remove('active');
    }
  };

  // Event Handlers
  const onNoteAddedHandler = async (event) => {
    const { note } = event.detail;
    if (history.state?.view === 'modal') goBack();

    showLoading();
    try {
      const createdNote = await NotesApi.createNote({
        title: note.title,
        body: note.body,
      });
      await fetchNotes();
      currentTab = TAB_NOTES;
      currentQuery = '';
      navTabs?.setAttribute('active-tab', TAB_NOTES);
      bottomNav?.setAttribute('active-tab', TAB_NOTES);
      displayNotes();
      selectNote(createdNote);
    } catch (error) {
      showError('Failed to create note. Please try again.');
    } finally {
      hideLoading();
    }
  };

  const onNoteDeletedHandler = async (event) => {
    const { id } = event.detail;

    showLoading();
    try {
      await NotesApi.deleteNote(id);

      if (selectedNote?.id === id) {
        selectedNote = null;
        if (desktopNoteDetail) desktopNoteDetail.note = null;
        if (mobileNoteDetailComponent) mobileNoteDetailComponent.note = null;
        if (history.state?.view === 'detail') goBack();
      }

      await fetchNotes();
      displayNotes();
    } catch (error) {
      showError('Failed to delete note. Please try again.');
    } finally {
      hideLoading();
    }
  };

  const onNoteArchivedHandler = async (event) => {
    const { id } = event.detail;

    showLoading();
    try {
      await NotesApi.archiveNote(id);

      if (selectedNote?.id === id) {
        selectedNote = null;
        if (desktopNoteDetail) desktopNoteDetail.note = null;
      }

      await fetchNotes();
      displayNotes();
    } catch (error) {
      showError('Failed to archive note. Please try again.');
    } finally {
      hideLoading();
    }
  };

  const onNoteUnarchivedHandler = async (event) => {
    const { id } = event.detail;

    showLoading();
    try {
      await NotesApi.unarchiveNote(id);

      if (selectedNote?.id === id) {
        selectedNote = null;
        if (desktopNoteDetail) desktopNoteDetail.note = null;
      }

      await fetchNotes();
      displayNotes();
    } catch (error) {
      showError('Failed to unarchive note. Please try again.');
    } finally {
      hideLoading();
    }
  };

  const onSearchHandler = (event) => {
    const query = event.detail?.query ?? '';
    currentQuery = query;
    displayNotes();
  };

  const onTabChangedHandler = (event) => {
    const { tab } = event.detail;

    // Handle history for tabs
    if (tab === TAB_ARCHIVE) {
      pushHistory({ view: 'archive' });
    } else if (tab === TAB_NOTES && currentTab === TAB_ARCHIVE) {
      goBack();
      return;
    }

    currentTab = tab;
    selectedNote = null;
    if (desktopNoteDetail) desktopNoteDetail.note = null;
    bottomNav?.setAttribute('active-tab', tab);
    displayNotes();
  };

  const onNoteSelectedHandler = (event) => {
    const { note } = event.detail;

    if (window.innerWidth < BREAKPOINT_MD) {
      showMobileNoteDetail(note);
    } else {
      pushHistory({ view: 'detail', noteId: note.id });
      selectNote(note);
    }
  };

  // Event Listeners
  // Desktop
  newNoteBtn?.addEventListener('click', showNoteFormModal);
  themeToggle?.addEventListener('click', toggleTheme);
  desktopSearchBar?.addEventListener('search', onSearchHandler);
  navTabs?.addEventListener('tab-changed', onTabChangedHandler);

  // Mobile
  mobileThemeToggle?.addEventListener('click', toggleTheme);
  mobileSearchBar?.addEventListener('search', onSearchHandler);
  bottomNav?.addEventListener('tab-changed', onTabChangedHandler);
  bottomNav?.addEventListener('add-clicked', showNoteFormModal);

  // Global events
  document.addEventListener('note-added', onNoteAddedHandler);
  document.addEventListener('note-deleted', onNoteDeletedHandler);
  document.addEventListener('note-archived', onNoteArchivedHandler);
  document.addEventListener('note-unarchived', onNoteUnarchivedHandler);
  document.addEventListener('note-selected', onNoteSelectedHandler);
  document.addEventListener('back-pressed', () => goBack());
  document.addEventListener('form-closed', () => goBack());

  // Close modal on backdrop click
  noteFormModal?.addEventListener('click', (event) => {
    if (event.target === noteFormModal) {
      goBack();
    }
  });

  // Handle History Back (Popstate)
  window.addEventListener('popstate', (event) => {
    const state = event.state || {}; // Handle null state as empty object
    const view = state.view;

    // 1. Handle Modal
    if (view === 'modal') {
      if (noteFormModal && !noteFormModal.classList.contains('active')) {
        noteFormModal.classList.add('active');
        const container = noteFormModal.querySelector('.note-form-container');
        animateModalShow(container);
      }
    } else if (noteFormModal?.classList.contains('active')) {
      hideNoteFormModal();
    }

    // 2. Handle Detail / Selection
    if (view === 'detail' && state.noteId) {
      const note =
        activeNotes.find((n) => n.id === state.noteId) ||
        archivedNotes.find((n) => n.id === state.noteId);

      if (note) {
        selectNote(note);
        if (window.innerWidth < BREAKPOINT_MD && mobileNoteDetail) {
          mobileNoteDetail.classList.add('active');
          animateMobileDetailShow(mobileNoteDetail);
        }
      }
    } else if (view !== 'modal') {
      // Clear selection if not in detail view (and not opening a modal on top)
      if (mobileNoteDetail?.classList.contains('active')) {
        hideMobileNoteDetail();
      }

      if (selectedNote) {
        selectedNote = null;
        if (desktopNoteDetail) desktopNoteDetail.note = null;
        if (mobileNoteDetailComponent) mobileNoteDetailComponent.note = null;
        if (desktopNoteList) desktopNoteList.setSelectedNote(null);
      }
    }

    // 3. Handle Tab Switching
    if (view === 'archive') {
      if (currentTab !== TAB_ARCHIVE) {
        const tab = TAB_ARCHIVE;
        currentTab = tab;
        navTabs?.setAttribute('active-tab', tab);
        bottomNav?.setAttribute('active-tab', tab);
        displayNotes();
      }
    } else if (currentTab === TAB_ARCHIVE) {
      // If state is NOT archive, but current tab IS archive -> Switch to Notes
      const tab = TAB_NOTES;
      currentTab = tab;
      navTabs?.setAttribute('active-tab', tab);
      bottomNav?.setAttribute('active-tab', tab);
      displayNotes();
    }
  });

  // Handle Esc Key
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (noteFormModal?.classList.contains('active')) {
        goBack();
      }
    }
  });

  // Initialize
  const init = async () => {
    initTheme();
    await fetchNotes();
    displayNotes();
  };

  init();
};

export default home;
